/**
 * 视频 provider × model 约束派生 hook(P2-3 拆解 + Coup-4,2026-05-30)。
 *
 * 从 VideoCreatePage 抽出:按素材推断 generationMode、智能选型、查 selectedCap、手动模式联动收窄
 * ratio/resolution/duration/mode/audio。海报有 useProviderConstraints,视频此前内联在页面里;
 * 这里收口成视频自己的 hook(capability schema 跟海报不同,无法真共享,但对齐"约束逻辑进 hook"的结构)。
 */
import { useEffect, useMemo } from "react";

import { toast } from "@/shared/hooks/use-toast";
import { videoProviderLabel } from "@/shared/lib/video/labels";

import type { FormState } from "../model/videoCreateTypes";
import type {
  GenerationMode,
  VideoCapabilitiesResponse,
  VideoProviderCapability,
  VideoResolution,
} from "../api/videoService";

/**
 * 按素材自动推断 generationMode。
 * 都没 → T2V;只首帧 → I2V;首+末 → I2V_FIRST_LAST
 */
export function inferMode(firstFrameUrl?: string, lastFrameUrl?: string): GenerationMode {
  if (firstFrameUrl && lastFrameUrl) return "I2V_FIRST_LAST";
  if (firstFrameUrl) return "I2V";
  return "T2V";
}

/**
 * 素材 / 智能选型 / 手动选型 → 最终 generationMode(从 hook 抽出成纯函数,可单测)。
 */
export function resolveEffectiveMode(args: {
  hasPanelFrames: boolean;
  useAutoModel: boolean;
  inferredMode: GenerationMode;
  formMode: GenerationMode;
  firstFrameUrl?: string | null;
  manualCap?: VideoProviderCapability;
}): GenerationMode {
  const { hasPanelFrames, useAutoModel, inferredMode, formMode, firstFrameUrl, manualCap } = args;
  // 有每镜首帧 → 强制 I2V(手动/智能都是),否则手动选了 t2v 模型会忽略首帧出废片
  if (hasPanelFrames) return "I2V";
  if (useAutoModel) return inferredMode;
  /**
   * ★手动选型 + **全局**首帧(图片/海报直接去视频)—— 与上面 hasPanelFrames 同一个待遇。
   *
   * 0826 马致博实证:图片直接去视频、手动选了 Seedance,却被提交闸拦下并提示
   * 「请在『高级』里换图生视频(I2V)模型(如 Seedance)」——**他选的就是 Seedance**。
   * 根因是这里只读 `form.generationMode`(用户没手动改过就一直是默认 T2V),完全不看
   * 有没有首帧;而每镜首帧那条早就强制 I2V 了。同一个「有首帧」的事实,两条路径待遇不同。
   *
   * 只在所选模型**确实支持**推断出的模式时才升档;不支持(如 wan t2v)则保持用户所选,
   * 那条提交闸的「换个 I2V 模型」提示在那种情况下才是对的 —— 反向锁钉在测试里。
   */
  if (firstFrameUrl && manualCap?.supportedGenerationModes.includes(inferredMode)) {
    return inferredMode;
  }
  return formMode;
}

/**
 * 切模型后把当前时长按新模型 durationRange **就近夹取**(#23)。
 * 旧逻辑无条件重置到 minD → 用户想要长视频却被打回最短;改成 clamp 到最近的合法值:
 * 超上限夹到 maxD、低于下限夹到 minD、范围内不变。
 */
export function clampDuration(durationSec: number, range: [number, number]): number {
  const [minD, maxD] = range;
  return Math.min(maxD, Math.max(minD, durationSec));
}

/**
 * 智能 model 选择 — 按素材 + 用户需求自动 pick provider × model。
 * 规则(企业级 toB,优先效果):
 *  - 含音频(generateAudio=true)→ Seedance 2.0 Pro(唯一支持)
 *  - I2V_FIRST_LAST 模式 → Seedance 2.0 Pro(唯一支持)
 *  - 其余(I2V / T2V)→ Seedance 2.0 Fast
 *  - caps 里没有 seedance 才回落 wan2.7 / 第一个 provider
 *
 * ★T2V 从 wan2.7 改判 Seedance Fast(2026-08-10)。原规则写着「T2V → Wan 2.7(文案理解 +
 * 多镜头叙事强项)」,读起来很合理,实际后果是:**智能选型默认开**(useAutoModel: true)、
 * 多数创建没有首帧 = T2V —— 于是**所有片子恒落 wan**,页面上那个 provider: "seedance"
 * 默认值只在用户手动关掉智能选型后才生效。而动作库 / 人物身份锁 / 参考音频都是 seedance
 * 独有能力,默认落 wan 等于默认把它们关掉(ai 侧 route_chunk_model 有段级升档补救,但只在
 * 「段里真带了参考」时才触发,补不全,还会让一条片子里混两个引擎的质感)。
 */
/**
 * D 批(2026-09-13):智能选型的**能力约束**(自由创作直发用)。
 *
 * ★30s / 1080P 自动升引擎「别硬编」的落点:自由创作单段直发选了 30s(或 1080P)时,默认的
 *   2.0 Fast(单段 ≤15s、无 1080P)装不下 —— 这里按 `durationRange` / `supportedResolutions`
 *   把选型收窄到**能出的最便宜档**(现网即 Seedance 2.5,duration_range 4-30)。判据全来自
 *   能力表,型号 id 一个都不写死;编排流(briefDuration 定档、多镜分段)不传 minDurationSec,
 *   避免拿「成片总时长」去要求单次调用一口气出那么长。缺省(不传)= 旧行为逐字不变(单测靠这个)。
 */
export interface AutoPickConstraints {
  /** 单次 provider 调用要出的最短秒数(超过默认档上限就升档);编排流不传。 */
  minDurationSec?: number;
  /** 目标分辨率(默认档不支持就升到支持它的档);拿不到不约束。 */
  resolution?: VideoResolution;
}

export function autoPickModel(
  caps: VideoCapabilitiesResponse,
  mode: GenerationMode,
  generateAudio: boolean,
  constraints?: AutoPickConstraints
): { provider: string; model: string } {
  const minDur = constraints?.minDurationSec;
  const wantRes = constraints?.resolution;
  /** 该档能否满足时长 / 分辨率约束(无约束恒真 = 旧行为,现有单测靠此保持绿)。 */
  const meets = (p: VideoProviderCapability) =>
    (minDur == null || p.durationRange[1] >= minDur) &&
    (wantRes == null || p.supportedResolutions.includes(wantRes));
  const find = (predicate: (p: VideoProviderCapability) => boolean) =>
    caps.providers.find(predicate);
  /**
   * 合格档里挑**最便宜**的那个(同价按列表顺序,即保持既有偏好)。
   *
   * ★2026-08-19 两次修正,记全免得再犯:
   *  ① 原写法是 `p.model.includes("260128")` —— 把「哪个型号支持出音频」钉在**型号 id 的
   *     一段数字**上。Seedance 2.5 的 id 是 `doubao-seedance-2-5-`**`260628`**,那条判断
   *     **永远认不出新型号**,而且失效时不报错、只是默默选了旧型号。
   *  ② 但只改成 `supportsGenerateAudio` 还不够:那样「选哪个」就完全由 `caps.providers`
   *     的**列表顺序**决定(`find` 取第一个命中)—— **等于把定价钉在数组顺序上**。
   *     下一个人为别的原因重排、或在前面插一个更贵的型号,就会**静默改掉所有带音频视频
   *     的单价**,而他不会觉得自己在改定价。
   * ⇒ 所以按 `creditsPerSecond` 显式取最低价:用户**没做任何选择**时,不该被换到更贵的档。
   *   未定价(null)排最后 —— 价都不知道的型号不配当自动选型的默认。
   */
  const cheapestOf = (predicate: (p: VideoProviderCapability) => boolean) => {
    const price = (p: VideoProviderCapability) => p.creditsPerSecond ?? Number.POSITIVE_INFINITY;
    return caps.providers
      .filter(predicate)
      .reduce<
        VideoProviderCapability | undefined
      >((best, p) => (best === undefined || price(p) < price(best) ? p : best), undefined);
  };
  if (generateAudio) {
    const c = cheapestOf((p) => p.provider === "seedance" && p.supportsGenerateAudio && meets(p));
    if (c) return { provider: c.provider, model: c.model };
  }
  if (mode === "I2V_FIRST_LAST") {
    const c = cheapestOf(
      (p) =>
        p.provider === "seedance" &&
        p.supportedGenerationModes.includes("I2V_FIRST_LAST") &&
        meets(p)
    );
    if (c) return { provider: c.provider, model: c.model };
  }
  // I2V / T2V 默认 Seedance Fast(性价比档;Pro 只留给上面两条真需要它的分支)。
  // ★但 Fast 装不下约束(30s / 1080P)时升到**满足约束的最便宜** seedance —— 不写死型号。
  const fast = find((p) => p.provider === "seedance" && p.model.includes("fast"));
  if (fast && meets(fast)) return { provider: fast.provider, model: fast.model };
  const upgraded = cheapestOf((p) => p.provider === "seedance" && meets(p));
  if (upgraded) return { provider: upgraded.provider, model: upgraded.model };
  // 无 fast / 无满足约束的档:退回任意 seedance(约束下都不满足时的兜底,不硬崩)
  const seedance = find((p) => p.provider === "seedance");
  if (seedance) return { provider: seedance.provider, model: seedance.model };
  // seedance 不在 caps 里(能力表被裁/套餐不含)才回落 wan —— 不硬崩
  const wan = find((p) => p.provider === "wan2.7" && p.model.includes("t2v"));
  if (wan) return { provider: wan.provider, model: wan.model };
  // 兜底:任意第一个
  const fallback = caps.providers[0];
  return { provider: fallback.provider, model: fallback.model };
}

export interface VideoProviderConstraints {
  hasPanelFrames: boolean;
  inferredMode: GenerationMode;
  effectiveMode: GenerationMode;
  effectiveModel: { provider: string; model: string };
  selectedCap: VideoProviderCapability | undefined;
}

export function useVideoProviderConstraints(
  caps: VideoCapabilitiesResponse | null,
  form: FormState,
  setForm: React.Dispatch<React.SetStateAction<FormState>>
): VideoProviderConstraints {
  // storyboard 每镜有自己的首帧(panelFirstFrameKeys)→ 必走 I2V + I2V 能力的模型,
  // 否则像 wan2.7 t2v 那样忽略首帧凭空生成,既丢风格一致性又出废片。
  const hasPanelFrames = (form.panelFirstFrameKeys ?? []).some((k) => !!k);
  const inferredMode = hasPanelFrames ? "I2V" : inferMode(form.firstFrameUrl, form.lastFrameUrl);
  const effectiveMode: GenerationMode = resolveEffectiveMode({
    hasPanelFrames,
    useAutoModel: form.useAutoModel,
    inferredMode,
    formMode: form.generationMode,
    firstFrameUrl: form.firstFrameUrl,
    manualCap: caps?.providers.find((p) => p.provider === form.provider && p.model === form.model),
  });

  const effectiveModel = useMemo(() => {
    // model 兜 ""(返回类型 {provider:string; model:string} 不可空):旧草稿恢复时 form.model 可能 undefined,
    // 否则手动模式下游(徽标 .slice / 提交 payload)读 undefined.model 崩(2026-06-13 草稿点开「功能构建中」根因)。
    if (!caps) return { provider: form.provider, model: form.model ?? "" };
    if (form.useAutoModel) {
      // D 批:直发流(未选时长档)按单段秒数 + 分辨率收窄选型 —— 30s / 1080P 自动升引擎(2.5)。
      // 编排流(briefDuration 已定档)不传 minDurationSec:多镜分段,别拿成片总时长要求单次调用出那么长。
      return autoPickModel(caps, effectiveMode, form.generateAudio, {
        minDurationSec: form.briefDuration ? undefined : form.durationSec,
        resolution: form.resolution,
      });
    }
    return { provider: form.provider, model: form.model ?? "" };
  }, [
    caps,
    form.useAutoModel,
    form.provider,
    form.model,
    effectiveMode,
    form.generateAudio,
    form.briefDuration,
    form.durationSec,
    form.resolution,
  ]);

  const selectedCap: VideoProviderCapability | undefined = useMemo(() => {
    if (!caps) return undefined;
    return caps.providers.find(
      (p) => p.provider === effectiveModel.provider && p.model === effectiveModel.model
    );
  }, [caps, effectiveModel.provider, effectiveModel.model]);

  // 手动模式切换 provider 时,联动收窄 ratio/resolution/duration
  useEffect(() => {
    if (!selectedCap || form.useAutoModel) return;
    let durationToast: string | null = null;
    setForm((f) => {
      const next = { ...f };
      if (!selectedCap.supportedResolutions.includes(f.resolution as VideoResolution)) {
        next.resolution = selectedCap.supportedResolutions[0];
      }
      if (!selectedCap.supportedRatios.includes(f.ratio)) {
        next.ratio = selectedCap.supportedRatios[0];
      }
      if (!selectedCap.supportedGenerationModes.includes(f.generationMode)) {
        next.generationMode = selectedCap.supportedGenerationModes[0];
      }
      const [minD, maxD] = selectedCap.durationRange;
      if (f.durationSec < minD || f.durationSec > maxD) {
        // #23:就近 clamp(不再无条件重置到最短),并提示按模型范围调整。
        const clamped = clampDuration(f.durationSec, selectedCap.durationRange);
        next.durationSec = clamped;
        // OEM H2:对客名走档位名(老后端 label 是真名 → 回落镜像表)。
        const capName = videoProviderLabel(
          selectedCap.provider,
          selectedCap.model,
          selectedCap.tier ? selectedCap.label : null
        );
        durationToast = `已按【${capName}】支持范围将时长调整为 ${clamped} 秒`;
      }
      if (f.generateAudio && !selectedCap.supportsGenerateAudio) {
        next.generateAudio = false;
      }
      return next;
    });
    if (durationToast) {
      toast({ title: "时长已调整", description: durationToast });
    }
  }, [selectedCap, form.useAutoModel, setForm]);

  return { hasPanelFrames, inferredMode, effectiveMode, effectiveModel, selectedCap };
}
